import React, { useRef, useState, useMemo } from 'react';
import { IconScout } from '@/components/ui/images/IconScout';
import { NavIcon } from '@/components/ui/buttons';
import { SearchBar, UserDropdown, HelpDropdown, AppLauncher } from './';
import { colors } from '@/assets/styles/colors';
import { Link } from 'react-router-dom';
import { Text } from '@/components/ui/text';
import { LocalIcon } from '@/components/ui/images/LocalIcon';
import { Divider } from '@/components/ui/separators';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { useTranslation } from '@/i18n';
import type { RexSystem } from '@/api';


interface ToolsBarProps {
  isUserMenuOpen: boolean;
  isHelpMenuOpen: boolean;
  isAppLauncherOpen: boolean;
  searchTerm: string;
  setIsUserMenuOpen: (open: boolean) => void;
  setIsHelpMenuOpen: (open: boolean) => void;
  setIsAppLauncherOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleMobileMenuToggle: () => void;
  handleSearchChange: (term: string) => void;
  handleUserMenuClose: () => void;
  handleAppLauncherClose: () => void;
  handleLogout: () => void;
  handleIncidentPanelOpen: () => void;
  navigate: (path: string) => void;
  userName?: string;
  companyName?: string;
  isSSO: boolean;
  rexSystems: RexSystem[];
}

const MaxCompanyNameLength = 40;

const ToolsBar: React.FC<ToolsBarProps> = ({
  isUserMenuOpen,
  isHelpMenuOpen,
  isAppLauncherOpen,
  searchTerm,
  setIsUserMenuOpen,
  setIsHelpMenuOpen,
  setIsAppLauncherOpen,
  handleMobileMenuToggle,
  handleSearchChange,
  handleUserMenuClose,
  handleAppLauncherClose,
  handleLogout,
  handleIncidentPanelOpen,
  navigate,
  userName = 'Nombre Apellido',
  companyName,
  isSSO,
  rexSystems,
}) => {
  const { t } = useTranslation();
  const helpButtonRef = useRef<HTMLDivElement>(null);
  const incidentButtonRef = useRef<HTMLDivElement>(null);
  const companyNameRef = useRef<HTMLDivElement>(null);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [showIncidentTooltip, setShowIncidentTooltip] = useState(false);
  const [showCompanyTooltip, setShowCompanyTooltip] = useState(false);

  const truncatedCompanyName = useMemo(() => {
    if (!companyName) return '';
    return companyName.length > MaxCompanyNameLength
      ? `${companyName.substring(0, MaxCompanyNameLength)}...`
      : companyName;
  }, [companyName]);

  return (
    <div className="max-w-full px-4">
      <div className="flex items-center justify-between top-nav-height">
        <div className="flex items-center space-x-3">
          <NavIcon
            icon={<IconScout name="bars" size="md" color={colors.primary.general} />}
            onClick={handleMobileMenuToggle}
            className="mobile-menu-button lg:hidden"
          />

          {isSSO && rexSystems.length > 0 && (
            <div className="relative flex items-center">
              <NavIcon
                icon={<LocalIcon name="grid" data-testid="grid-icon-toolsbar" />}
                onClick={() => setIsAppLauncherOpen(prev => !prev)}
              />

              {isAppLauncherOpen && (
                <AppLauncher
                  rexSystems={rexSystems}
                  onClose={handleAppLauncherClose}
                />
              )}
              <Divider orientation="v" />
            </div>
            
          )}

          <div className="flex items-center gap-x-2">
            <Link to="/" className="flex items-center gap-x-2">
              <LocalIcon name="remxas" data-testid="remxas-icon-toolsbar" />
              <Text variant="subheader-regular" color={colors.grays.neutralFF} className="hidden sm:block">
                Asistencia
              </Text>
            </Link>

            {companyName && (
              <>
                <Text variant="subheader-regular" color={colors.grays.neutralFF} className="hidden sm:block">
                  |
                </Text>
                <div 
                  className="hidden sm:block relative"
                  ref={companyNameRef}
                  onMouseEnter={() => setShowCompanyTooltip(true)}
                  onMouseLeave={() => setShowCompanyTooltip(false)}
                >
                  <Text variant="subheader-regular" color={colors.grays.neutralFF}>
                    {truncatedCompanyName}
                  </Text>
                  {showCompanyTooltip && companyName.length > MaxCompanyNameLength && (
                    <Tooltip 
                      body={companyName} 
                      anchorRef={companyNameRef} 
                      placement="bottom"
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />

        <div className="flex items-center space-x-2">
          <div 
            className="relative"
            ref={incidentButtonRef}
            onMouseEnter={() => setShowIncidentTooltip(true)}
            onMouseLeave={() => setShowIncidentTooltip(false)}
          >
            <NavIcon
              icon={<IconScout name="alert" size="lg" color={colors.primary.general} />}
              onClick={handleIncidentPanelOpen}
            />
            {showIncidentTooltip && (
              <Tooltip 
                body={t('attendance:incident.title')} 
                anchorRef={incidentButtonRef} 
                placement="bottom"
              />
            )}
          </div>

          <div 
            className="relative"
            ref={helpButtonRef}
            onMouseEnter={() => setShowHelpTooltip(true)}
            onMouseLeave={() => setShowHelpTooltip(false)}
          >
            <NavIcon
              icon={<IconScout name="questionCircle" size="lg" color={colors.primary.general} />}
              onClick={() => setIsHelpMenuOpen(!isHelpMenuOpen)}
            />
            {isHelpMenuOpen && (
              <HelpDropdown onClose={() => setIsHelpMenuOpen(false)} />
            )}
            {showHelpTooltip && !isHelpMenuOpen && (
              <Tooltip 
                body={t('common:navigation.helpCenter')} 
                anchorRef={helpButtonRef} 
                placement="bottom"
              />
            )}
          </div>

          <NavIcon
            icon={<IconScout name="bell" size="lg" color={colors.primary.general} />}
          />

          <div className="relative">
            <NavIcon
              icon={
                <span className="flex items-center space-x-1">
                  <IconScout name="user" size="lg" color={colors.primary.general} />
                  <IconScout name="angleDown" size="lg" color={colors.primary.general} />
                </span>
            }
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            />

            {isUserMenuOpen && (
              <UserDropdown
                userName={userName}
                onEditProfile={() => {
                  handleUserMenuClose();
                  void navigate('/change-password');
                }}
                onLogout={() => {
                  handleUserMenuClose();
                  handleLogout();
                }}
                onHelpCenter={() => {
                  handleUserMenuClose();
                  window.open('https://ayudabnovus.rexmas.com/es/', '_blank', 'noopener,noreferrer');
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsBar;
